import type { PanelField } from '../../components/panel-fields.js';
import type { TestCaseDetail } from '../../lib/catalog-api.js';

// #28: Die Feldliste des Testfalls. Anlage-Panel und Detail-Ansicht rendern hieraus (Spec 2.1);
// testcase-fields.test.ts stellt sie gegen TestCaseCreateSchema.
export type TestCaseDraft = {
  title_de: string;
  title_en: string;
  precondition_de: string | null;
  precondition_en: string | null;
  expected_de: string | null;
  expected_en: string | null;
  polarity: 'positiv' | 'negativ' | null;
  readiness: 'bereit' | 'in_arbeit' | 'nicht_im_scope';
  default_tester_id: string | null;
};

export const emptyTestCaseDraft: TestCaseDraft = {
  title_de: '',
  title_en: '',
  precondition_de: null,
  precondition_en: null,
  expected_de: null,
  expected_en: null,
  polarity: null,
  readiness: 'in_arbeit',
  default_tester_id: null,
};

export function toTestCaseDraft(tc: TestCaseDetail): TestCaseDraft {
  return {
    title_de: tc.title_de,
    title_en: tc.title_en,
    precondition_de: tc.precondition_de,
    precondition_en: tc.precondition_en,
    expected_de: tc.expected_de,
    expected_en: tc.expected_en,
    polarity: tc.polarity,
    readiness: tc.readiness,
    default_tester_id: tc.default_tester_id,
  };
}

export function testCaseFields(
  t: (k: string) => string,
  testers: { user_id: string; name: string }[],
): PanelField<TestCaseDraft>[] {
  return [
    {
      key: 'title_de',
      labelKey: t('catalog.fields.titleDe'),
      kind: 'text',
      get: (d) => d.title_de,
      set: (d, v) => ({ ...d, title_de: v }),
    },
    {
      key: 'title_en',
      labelKey: t('catalog.fields.titleEn'),
      kind: 'text',
      get: (d) => d.title_en,
      set: (d, v) => ({ ...d, title_en: v }),
    },
    {
      key: 'precondition_de',
      labelKey: `${t('catalog.fields.precondition')} (DE)`,
      kind: 'textarea',
      get: (d) => d.precondition_de ?? '',
      set: (d, v) => ({ ...d, precondition_de: v || null }),
    },
    {
      key: 'precondition_en',
      labelKey: `${t('catalog.fields.precondition')} (EN)`,
      kind: 'textarea',
      get: (d) => d.precondition_en ?? '',
      set: (d, v) => ({ ...d, precondition_en: v || null }),
    },
    {
      key: 'expected_de',
      labelKey: `${t('catalog.fields.expected')} (DE)`,
      kind: 'textarea',
      get: (d) => d.expected_de ?? '',
      set: (d, v) => ({ ...d, expected_de: v || null }),
    },
    {
      key: 'expected_en',
      labelKey: `${t('catalog.fields.expected')} (EN)`,
      kind: 'textarea',
      get: (d) => d.expected_en ?? '',
      set: (d, v) => ({ ...d, expected_en: v || null }),
    },
    {
      key: 'polarity',
      labelKey: t('catalog.fields.polarity'),
      kind: 'select',
      get: (d) => d.polarity,
      set: (d, v) => ({ ...d, polarity: (v as TestCaseDraft['polarity']) ?? null }),
      options: [
        { value: 'positiv', label: t('catalog.polarity.positiv') },
        { value: 'negativ', label: t('catalog.polarity.negativ') },
      ],
      allowEmpty: true,
      emptyLabel: t('catalog.actions.none'),
    },
    {
      key: 'readiness',
      labelKey: t('catalog.fields.readiness'),
      kind: 'select',
      // Pflichtfeld mit Vorgabe „in_arbeit" — deshalb ohne allowEmpty.
      get: (d) => d.readiness,
      set: (d, v) => ({ ...d, readiness: (v as TestCaseDraft['readiness']) ?? 'in_arbeit' }),
      options: [
        { value: 'bereit', label: t('catalog.readiness.bereit') },
        { value: 'in_arbeit', label: t('catalog.readiness.in_arbeit') },
        { value: 'nicht_im_scope', label: t('catalog.readiness.nicht_im_scope') },
      ],
    },
    {
      key: 'default_tester_id',
      labelKey: t('catalog.fields.defaultTester'),
      kind: 'select',
      get: (d) => d.default_tester_id,
      set: (d, v) => ({ ...d, default_tester_id: v }),
      options: testers.map((u) => ({ value: u.user_id, label: u.name })),
      searchable: true,
      allowEmpty: true,
      emptyLabel: t('catalog.actions.none'),
    },
  ];
}
